import {
  type DxfBlocksSection,
  type DxfDocument,
  type DxfEntitiesSection,
  type DxfEntity,
  type DxfGroup,
  type DxfObjectsSection,
  type DxfPoint3D,
  type DxfSection,
  type DxfSupportedEntity,
  type DxfTablesSection,
  isSupportedEntityType,
  point3D,
} from "./types";

import { parseSupportedEntity, splitEntities } from "./entityParser";
import { foldComplexEntities } from "./entityGrouping";
import {
  parseTablesSection,
  parseBlocksSection,
  parseObjectsSection,
  parseDimStyleEntry,
  parseMultileaderStyleEntry,
  parseDictionaryEntry,
  parseDynamicBlockObject,
} from "./sectionParsers";
import { StringPool } from "./stringPool";
import {
  buildBlockHandleMap,
  buildLineTypeHandleMap,
  buildTextStyleHandleMap,
  resolveMultileaderStyleHandles,
  resolveDxfMLeaderHandles,
} from "./handleResolution";
import { tokenizeDxfStream } from "./streamingParser";

// Silence unused-import warnings for the section helpers that callers
// outside this file import via `from "./parser"` re-exports. Keeping
// them in the import list above documents the section-parser surface
// of this module.
void parseDimStyleEntry;
void parseMultileaderStyleEntry;
void parseDictionaryEntry;

function parseEntitiesSection(
  groups: DxfGroup[],
  stringPool: StringPool,
): DxfEntitiesSection {
  const rawEntities = splitEntities(groups).map((entityGroups): DxfEntity => {
    const header = entityGroups[0];

    if (!header || header.code !== 0) {
      throw new Error("Entity record is missing a type header");
    }

    if (isSupportedEntityType(header.value)) {
      return parseSupportedEntity(entityGroups, stringPool);
    }

    return {
      kind: "unsupported-entity",
      type: header.value,
      groups: entityGroups,
    };
  });

  // Attach each complex entity's sibling child run to its owner: an INSERT
  // with group 66 = 1 swallows the following ATTRIBs up to and including the
  // SEQEND, and a legacy heavy POLYLINE swallows its VERTEX run the same way
  // (Autodesk DXF reference, "Complex entities"). Consuming them here rather
  // than leaving them as siblings lets downstream consumers (the
  // drawing-model bridge, the writer) operate on ONE logical entity. Orphan
  // children — a stray ATTRIB / VERTEX / SEQEND with no owner — survive in
  // the list as stand-alone entities so nothing is lost. The fold itself is
  // shared with the BLOCKS and streaming paths (./entityGrouping).
  const entities = foldComplexEntities(rawEntities);

  return {
    kind: "entities-section",
    entities,
  };
}

/**
 * Eager DxfGroup tokenizer.
 *
 * Backed by the streaming generator (tokenizeDxfStream). Materialises
 * the full group array — kept for backward compat with callers that
 * still want a flat list (writer fixtures, benchmarks). New code that
 * cares about peak heap should use tokenizeDxfStream directly.
 */
export function tokenizeDxf(source: string): DxfGroup[] {
  const out: DxfGroup[] = [];
  for (const g of tokenizeDxfStream(source)) {
    out.push(g);
  }
  return out;
}

export function parseDxfDocument(source: string): DxfDocument {
  const groups = tokenizeDxf(source);
  const sections: DxfSection[] = [];

  // Per-parse-call string pool. Interns layer/linetype/styleName/blockName
  // etc. across ENTITIES and BLOCKS child entities so a ~5000-entity file
  // with only ~50 unique values for these fields stops allocating
  // thousands of duplicate JS strings. Scope is strictly this one call —
  // no cross-document sharing.
  const stringPool = new StringPool();

  // ── Pass 1: tokenise sections and parse TABLES/OBJECTS/ENTITIES ──
  for (let index = 0; index < groups.length; ) {
    const group = groups[index];

    if (!group) {
      break;
    }

    if (group.code === 0 && group.value === "EOF") {
      break;
    }

    if (group.code !== 0 || group.value !== "SECTION") {
      throw new Error(
        `Unexpected top-level group ${group.code}:${group.value}`,
      );
    }

    const sectionNameGroup = groups[index + 1];

    if (!sectionNameGroup || sectionNameGroup.code !== 2) {
      throw new Error("SECTION must be followed by a section name group (2)");
    }

    const sectionGroups: DxfGroup[] = [];
    index += 2;

    while (index < groups.length) {
      const current = groups[index];

      if (!current) {
        break;
      }

      if (current.code === 0 && current.value === "ENDSEC") {
        index += 1;
        break;
      }

      sectionGroups.push(current);
      index += 1;
    }

    if (sectionNameGroup.value === "ENTITIES") {
      sections.push(parseEntitiesSection(sectionGroups, stringPool));
    } else if (sectionNameGroup.value === "TABLES") {
      sections.push(parseTablesSection(sectionGroups));
    } else if (sectionNameGroup.value === "OBJECTS") {
      sections.push(parseObjectsSection(sectionGroups));
    } else if (sectionNameGroup.value === "BLOCKS") {
      sections.push(parseBlocksSection(sectionGroups, stringPool));
    } else {
      sections.push({
        kind: "raw-section",
        name: sectionNameGroup.value,
        groups: sectionGroups,
      });
    }
  }

  // ── Pass 2: resolve handle-based cross-references ──
  // MLEADERSTYLE entries (OBJECTS) and MLEADER entities reference STYLE /
  // LTYPE / BLOCK records by handle (group 340/341/342/343 for the style,
  // group 340 inside CONTEXT_DATA for the entity). The first pass captured
  // those as opaque handles because the target entries may live in
  // sections parsed later. Resolution is shared with the streaming parser
  // via resolveDxfMLeaderHandles so both stay byte-identical.
  resolveDxfMLeaderHandles(sections);

  return {
    sections,
  };
}

export function getEntitiesSection(
  document: DxfDocument,
): DxfEntitiesSection | null {
  return (
    document.sections.find(
      (section): section is DxfEntitiesSection =>
        section.kind === "entities-section",
    ) ?? null
  );
}

export function getTablesSection(
  document: DxfDocument,
): DxfTablesSection | null {
  return (
    document.sections.find(
      (section): section is DxfTablesSection =>
        section.kind === "tables-section",
    ) ?? null
  );
}

export function getObjectsSection(
  document: DxfDocument,
): DxfObjectsSection | null {
  return (
    document.sections.find(
      (section): section is DxfObjectsSection =>
        section.kind === "objects-section",
    ) ?? null
  );
}

export function getBlocksSection(
  document: DxfDocument,
): DxfBlocksSection | null {
  return (
    document.sections.find(
      (section): section is DxfBlocksSection =>
        section.kind === "blocks-section",
    ) ?? null
  );
}

export function getSupportedEntities(
  document: DxfDocument,
): DxfSupportedEntity[] {
  const entitiesSection = getEntitiesSection(document);

  if (!entitiesSection) {
    return [];
  }

  return entitiesSection.entities.filter(
    (entity): entity is DxfSupportedEntity =>
      entity.kind === "supported-entity",
  );
}

/**
 * NON-DRAINING lazy variant of `getSupportedEntities` — yields each supported
 * top-level entity WITHOUT materializing a filtered array (and WITHOUT the second
 * copy a `[...getSupportedEntities(document)]` spread incurs). Unlike
 * `getSupportedEntitiesDraining` it does NOT mutate/clear the section, so it is
 * safe to run BEFORE the streaming drain — e.g. capturing VIEWPORT XDATA authority
 * up front (@codex #486 P2). Iterating lazily avoids the transient full-array
 * allocation that would otherwise reintroduce the peak-memory cost the streaming
 * path was designed to avoid on 685k-entity drawings.
 */
export function* iterateSupportedEntities(
  document: DxfDocument,
): Generator<DxfSupportedEntity, void, undefined> {
  const entitiesSection = getEntitiesSection(document);
  if (!entitiesSection) return;
  for (const entity of entitiesSection.entities) {
    if (entity.kind === "supported-entity") yield entity;
  }
}

/**
 * Codex P5 / Option B follow-up (2026-05-18) — draining variant of
 * `getSupportedEntities`. Yields each supported entity AND clears the
 * corresponding slot in `document.sections[entities].entities` so V8
 * can reclaim the entity payload as the consumer processes the
 * generator. Final `arr.length = 0` zeros out the array so the entire
 * top-level entities array becomes a small empty shell after the
 * generator drains.
 *
 * **DESTRUCTIVE** — mutates `document.sections[entities].entities` in
 * place. Only safe when:
 *   - the document is owned exclusively by this consumer (worker
 *     thread, single-pass bridge), and
 *   - no later code in the same scope needs to re-read the entities.
 *
 * Memory impact: on a 685k-entity P35R drawing the
 * `document.sections[entities].entities` array holds ~150 MB of
 * DxfSupportedEntity references in steady state. Bridging via the
 * non-draining `getSupportedEntities` keeps the full array alive
 * until `dxfDoc = null` (post-bridge). Draining lets entity payloads
 * GC as the bridge emits chunks downstream.
 */
export function* getSupportedEntitiesDraining(
  document: DxfDocument,
): Generator<DxfSupportedEntity, void, undefined> {
  const entitiesSection = getEntitiesSection(document);
  if (!entitiesSection) return;
  const arr = entitiesSection.entities;
  for (let i = 0; i < arr.length; i++) {
    const entity = arr[i];
    // Detach the slot synchronously so the entity payload is eligible
    // for GC once the consumer's iteration body releases its yielded
    // reference. Cast is necessary because the typed array's element
    // type is non-null; we type-narrow with the kind check below.
    (arr as Array<DxfEntity | null>)[i] = null;
    if (entity && entity.kind === "supported-entity") {
      yield entity;
    }
  }
  // Hand the now-empty array shell back to GC by clearing its length.
  arr.length = 0;
}

export function defaultExtrusion(): DxfPoint3D {
  return point3D(0, 0, 1);
}

// Re-export the dynamic-block object shallow parser so the bridge layer
// (packages/drawing-model/src/dxfBridge.dynamic.ts) can rebuild an
// object from fresh metadata without importing from the private
// sectionParsers module. NOTE: per CLAUDE.md "split file imports" rule,
// this re-export is for external consumers — local callers inside the
// dxf-io package must still import directly from `./sectionParsers`.
export { parseDynamicBlockObject };

// Re-export the handle-resolution helpers so external consumers (and
// fixture builders) can rebuild the MLEADERSTYLE name resolution
// without reaching into the private module. Local callers inside
// `packages/dxf-io` must still import them directly from
// `./handleResolution` per CLAUDE.md "split file imports" rule.
export {
  buildBlockHandleMap,
  buildLineTypeHandleMap,
  buildTextStyleHandleMap,
  resolveMultileaderStyleHandles,
  resolveDxfMLeaderHandles,
};
